import React from 'react'
import Select, { components as rsComponents } from 'react-select'
import CreatableSelect from 'react-select/creatable'
import { useTranslation } from 'react-i18next'

const menuPortalStyle = (base) => ({ ...base, zIndex: 9999 })

const defaultStyles = {
  menuPortal: menuPortalStyle,
  control: (base, state) => ({
    ...base,
    minHeight: 40,
    borderRadius: 'var(--radius-md)',
    borderColor: state.isFocused ? 'var(--ring)' : 'var(--input)',
    backgroundColor: 'var(--card)',
    boxShadow: state.isFocused ? '0 0 0 1px var(--ring)' : 'none',
    '&:hover': { borderColor: 'var(--border)' },
    fontSize: 14,
  }),
  menu: (base) => ({
    ...base,
    zIndex: 50,
    borderRadius: 'var(--radius-md)',
    overflow: 'hidden',
    backgroundColor: 'var(--card)',
    border: '1px solid var(--border)',
  }),
  menuList: (base) => ({ ...base, padding: 4 }),
  option: (base, state) => ({
    ...base,
    borderRadius: 6,
    fontSize: 14,
    backgroundColor: state.isSelected
      ? 'color-mix(in oklab, var(--primary) 12%, transparent)'
      : state.isFocused
        ? 'var(--accent)'
        : 'transparent',
    color: 'var(--foreground)',
  }),
  placeholder: (base) => ({ ...base, color: 'var(--muted-foreground)', fontSize: 14 }),
  singleValue: (base) => ({ ...base, color: 'var(--foreground)', fontSize: 14 }),
  multiValue: (base) => ({
    ...base,
    borderRadius: 6,
    backgroundColor: 'color-mix(in oklab, var(--primary) 12%, transparent)',
  }),
  multiValueLabel: (base) => ({ ...base, color: 'var(--foreground)', fontSize: 13 }),
  input: (base) => ({ ...base, fontSize: 14, color: 'var(--foreground)' }),
}

function CompactMultiValue(props) {
  if (props.index > 0) return null
  return <rsComponents.MultiValue {...props} />
}

/** Affiche le 1er tag + « +n extra(s) » pour éviter une ligne trop longue en isMulti */
function CompactMultiValueContainer(props) {
  const { t } = useTranslation()
  const vals = props.selectProps?.value || []
  const n = vals.length
  const kids = React.Children.toArray(props.children).filter(Boolean)
  if (n <= 1) {
    return <rsComponents.MultiValueContainer {...props} />
  }
  const rest = n - 1
  const first = kids[0]
  const extraWord =
    rest > 1
      ? t('transferts.reservations.form.extras').toLowerCase()
      : t('transferts.reservations.form.extra').toLowerCase()
  return (
    <rsComponents.MultiValueContainer {...props}>
      {first}
      <span
        className='ml-1 inline-flex shrink-0 items-center rounded-md bg-[color-mix(in_oklab,var(--primary)_12%,transparent)] px-2 py-0.5 text-xs font-semibold text-[var(--primary)]'
      >
        +{rest} {extraWord}
      </span>
    </rsComponents.MultiValueContainer>
  )
}

/**
 * Liste déroulante avec recherche (react-select, isSearchable toujours actif).
 * options: { value, label }[]
 * value: valeur simple ou tableau si isMulti
 * creatable: affiche une action de création quand la recherche ne correspond à aucune option (react-select Creatable)
 */
export default function SearchableSelect({
  options = [],
  value,
  onChange,
  placeholder,
  isMulti = false,
  isClearable = true,
  isDisabled = false,
  className = '',
  id,
  'aria-label': ariaLabel,
  /** isMulti : n’affiche que le 1er choix + « +n extras » */
  compactMultiSelection = false,
  creatable = false,
  /** @param {string} inputValue */
  onCreateOption,
  /** @param {string} inputValue */
  formatCreateLabel,
  /** Optional react-select style overrides merged on top of defaults */
  styles: stylesOverride,
  /** Merge/override react-select components (e.g. custom Option) */
  components: componentsOverride,
  onInputChange,
  filterOption,
  ...rest
}) {
  const { t } = useTranslation()
  const resolvedPlaceholder = placeholder ?? t('transferts.common.choose')

  const findOne = (v) => {
    if (v === null || v === undefined) return null
    const flat = []
    for (const o of options) {
      if (Array.isArray(o.options)) flat.push(...o.options)
      else flat.push(o)
    }
    return flat.find((o) => String(o.value) === String(v)) ?? null
  }
  const selected = isMulti
    ? (Array.isArray(value) ? value.map(findOne).filter(Boolean) : [])
    : findOne(value) ??
      (creatable && value != null && value !== ''
        ? { value, label: String(value) }
        : null)

  const compactComponents =
    isMulti && compactMultiSelection
      ? {
          MultiValue: CompactMultiValue,
          MultiValueContainer: CompactMultiValueContainer,
        }
      : null

  const selectComponents = componentsOverride || compactComponents
    ? { ...(compactComponents || {}), ...(componentsOverride || {}) }
    : undefined

  const baseSelectStyles =
    isMulti && compactMultiSelection
      ? {
          ...defaultStyles,
          multiValue: (base) => ({
            ...defaultStyles.multiValue(base),
            maxWidth: 'min(220px, 50%)',
          }),
          multiValueLabel: (base) => ({
            ...defaultStyles.multiValueLabel(base),
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          }),
        }
      : defaultStyles

  const selectStyles = stylesOverride
    ? { ...baseSelectStyles, ...stylesOverride }
    : baseSelectStyles

  const SelectComponent =
    creatable && typeof onCreateOption === 'function' ? CreatableSelect : Select
  const createProps =
    creatable && typeof onCreateOption === 'function'
      ? {
          onCreateOption,
          formatCreateLabel:
            formatCreateLabel ||
            ((inputValue) => {
              const text = String(inputValue || '').trim()
              return text
                ? t('transferts.reservations.billing.addQuoted', { label: text })
                : t('transferts.reservations.billing.addEllipsis')
            }),
        }
      : {}

  return (
    <div className={className}>
      <SelectComponent
        inputId={id}
        aria-label={ariaLabel}
        options={options}
        value={selected}
        onChange={(opt) => {
          if (isMulti) {
            onChange?.(opt ? opt.map((o) => o.value) : [])
          } else {
            onChange?.(opt?.value ?? null)
          }
        }}
        placeholder={resolvedPlaceholder}
        isSearchable
        isClearable={isClearable}
        isDisabled={isDisabled}
        isMulti={isMulti}
        components={selectComponents}
        onInputChange={onInputChange}
        filterOption={filterOption}
        menuPortalTarget={typeof document !== 'undefined' ? document.body : null}
        menuPosition='fixed'
        styles={selectStyles}
        noOptionsMessage={() => t('transferts.common.noResults')}
        loadingMessage={() => t('transferts.common.loading')}
        {...createProps}
        {...rest}
      />
    </div>
  )
}
